import React from 'react';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';

const TermsOfUse = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <BackButton onClick={() => navigate(-1)} />
          <div className="text-center mt-4">
            <h1 className="text-3xl font-bold text-gray-900">Terms of Use</h1>
            <p className="text-gray-600 mt-2">Last updated: {new Date().toLocaleDateString()}</p>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="prose prose-lg max-w-none">
            <div className="text-center mb-8">
              <p className="text-gray-700 mb-4">
                Welcome to the BBA Portal ("Service"). These Terms of Use ("Terms") govern your access to and use of the Service provided by Builders Breakthrough Advisors ("BBA," "we," "us," or "our"). By accessing or using the Service, you agree to be bound by these Terms. If you do not agree, do not use the Service.
              </p>
            </div>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-700 mb-4">
                Your access to and use of the BBA Portal signifies your agreement to comply with and be legally bound by these Terms. If you do not accept these Terms, you may not access the Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
              <p className="text-gray-700 mb-4">
                The BBA Portal provides clients with access to business analytics dashboards, learning materials, downloadable documents, spreadsheet-based tools, and data-driven insights to support business growth and operational efficiency.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. User Accounts</h2>
              <div className="text-gray-700 space-y-4">
                <p>
                  To access the Service, you may be required to create an account. You agree to provide accurate, complete, and up-to-date information and to maintain the security of your account.
                </p>
                <p>
                  You are solely responsible for safeguarding your login credentials and for any activity that occurs under your account. Sharing your login information is strictly prohibited. Notify us immediately of any unauthorized use or suspected breach.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Acceptable Use</h2>
              <div className="text-gray-700 space-y-4">
                <p>You agree to use the Service only for lawful purposes. You will not:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Violate any applicable laws or regulations</li>
                  <li>Infringe upon the rights of others</li>
                  <li>Upload or distribute harmful, offensive, or inappropriate content</li>
                  <li>Attempt unauthorized access to the Service, other accounts, or networks</li>
                  <li>Disrupt or interfere with the Service or its infrastructure</li>
                  <li>Use the Service for commercial resale or redistribution without prior written consent</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Intellectual Property</h2>
              <div className="text-gray-700 space-y-4">
                <p>
                  All content, features, and functionality within the Service are the exclusive property of BBA and its licensors, and are protected by intellectual property laws.
                </p>
                <p>
                  You retain ownership of any content you upload or submit to the platform. By submitting content, you grant BBA a non-exclusive, royalty-free, worldwide license to use, store, reproduce, and distribute it solely for purposes related to providing the Service.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Privacy</h2>
              <p className="text-gray-700 mb-4">
                Your privacy matters to us. Please refer to our <a href="/privacy" className="text-blue-600 hover:text-blue-800 underline">Privacy Policy</a> for details on how we collect, use, and protect your information, including our SMS text messaging policies.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6A. SMS Text Messaging Terms</h2>
              <div className="text-gray-700 space-y-4">
                <p>By opting in to receive SMS text messages from Builders Breakthrough Advisors ("BBA"), you agree to the following terms and conditions:</p>
                
                <h3 className="text-xl font-medium text-gray-900 mb-3 mt-4">Consent and Opt-In</h3>
                <p>When you provide your mobile phone number and opt in to receive SMS messages, you expressly consent to receive automated text messages from BBA at the mobile number you provided. You represent that you are the authorized user of the mobile phone number provided and have the authority to consent to receive messages at that number.</p>

                <h3 className="text-xl font-medium text-gray-900 mb-3 mt-4">Message Types and Content</h3>
                <p>SMS messages may include:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Transactional messages related to your account or our services</li>
                  <li>Service alerts and notifications</li>
                  <li>Appointment reminders and scheduling updates</li>
                  <li>Promotional offers and marketing communications (if you have opted in to marketing)</li>
                  <li>Customer support communications</li>
                </ul>

                <h3 className="text-xl font-medium text-gray-900 mb-3 mt-4">Opt-Out Rights</h3>
                <p>You may opt out of receiving SMS messages at any time by replying "STOP" or "UNSUBSCRIBE" to any message. You may also opt out through your account settings or by contacting us directly. After opting out, you will receive one final confirmation message. Standard message and data rates may apply to all messages, including the opt-out confirmation.</p>
                <p className="font-semibold text-gray-900">Message and data rates may apply.</p>

                <h3 className="text-xl font-medium text-gray-900 mb-3 mt-4">No Purchase Necessary</h3>
                <p>Consent to receive SMS messages is not required as a condition of purchase. You may decline to receive SMS messages and still access and use the BBA Portal.</p>

                <h3 className="text-xl font-medium text-gray-900 mb-3 mt-4">Carrier and Delivery</h3>
                <p>BBA is not responsible for delayed or undelivered messages. Message delivery is subject to effective transmission from your wireless service provider/network operator. BBA does not guarantee delivery of SMS messages and will not be liable for any losses or damages resulting from failed or delayed message delivery.</p>

                <h3 className="text-xl font-medium text-gray-900 mb-3 mt-4">Help and Support</h3>
                <p>For help with SMS messages, reply "HELP" to any message, contact us at info@buildersbreakthrough.com, or call (919) 824-5744.</p>

                <h3 className="text-xl font-medium text-gray-900 mb-3 mt-4">Changes to SMS Terms</h3>
                <p>BBA reserves the right to modify these SMS terms at any time. Material changes will be communicated through the Service or via SMS. Continued receipt of messages after changes constitutes acceptance of the modified terms.</p>

                <p className="mt-4">For detailed information about how we handle your mobile number and SMS data, please review our <a href="/privacy" className="text-blue-600 hover:text-blue-800 underline">Privacy Policy</a>, particularly Section 12 regarding SMS Text Messaging.</p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Termination</h2>
              <div className="text-gray-700 space-y-4">
                <p>
                  We reserve the right to suspend or terminate your account at any time, without prior notice, for conduct that violates these Terms or is otherwise harmful to the Service or other users.
                </p>
                <p>
                  Upon termination, your access to the Service will cease immediately. If you choose to discontinue use, you may do so at any time.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Limitation of Liability</h2>
              <div className="text-gray-700 space-y-4">
                <p>
                  To the fullest extent permitted by law, BBA and its affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service.
                </p>
                <p>
                  Our total liability for any claims relating to the Service shall not exceed the amount you paid, if any, for access to the Service during the twelve (12) months prior to the event giving rise to the claim.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Disclaimer</h2>
              <div className="text-gray-700 space-y-4">
                <p>
                  The Service is provided on an "as-is" and "as-available" basis. BBA makes no warranties, express or implied, regarding the accuracy, reliability, or availability of the Service. We do not guarantee that the Service will be uninterrupted, secure, or error-free.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Governing Law</h2>
              <p className="text-gray-700 mb-4">
                These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law principles. Failure to enforce any part of these Terms does not constitute a waiver of any rights.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Changes to Terms</h2>
              <p className="text-gray-700 mb-4">
                We may update these Terms from time to time. If material changes are made, we will provide notice via the Service or other communication channels. Continued use of the Service after changes take effect constitutes your acceptance of the revised Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Contact Information</h2>
              <p className="text-gray-700 mb-4">
                For questions or concerns regarding these Terms, please contact:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700">
                  <strong>Email:</strong> info@buildersbreakthrough.com<br />
                  <strong>Address:</strong> 5102 Durham-Chapel Hill Blvd, Durham, NC 27707<br />
                  <strong>Phone:</strong> (919) 824-5744
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfUse; 